import type { Transformer } from "@lexical/markdown";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $addUpdateTag,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  LexicalCommand,
  SKIP_DOM_SELECTION_TAG,
} from "lexical";
import { useEffect } from "react";
import { deserializeFromMarkdown } from "../../../RichTextEditor/utils/serialization";

export type ReplaceTextPayload = Readonly<{ text: string; focus?: boolean }>;
export type InsertTextPayload = Readonly<{ text: string }>;
export type ReplaceFromMarkdownPayload = Readonly<{
  markdown: string;
  focus?: boolean;
}>;

export const REPLACE_TEXT_COMMAND: LexicalCommand<ReplaceTextPayload> =
  createCommand();

export const INSERT_TEXT_COMMAND: LexicalCommand<InsertTextPayload> =
  createCommand();

export const REPLACE_FROM_MARKDOWN_COMMAND: LexicalCommand<ReplaceFromMarkdownPayload> =
  createCommand();

type TextReplacePluginProps = {
  /**
   * When set (rich-text mode), registers `REPLACE_FROM_MARKDOWN_COMMAND` so the
   * document can be replaced from markdown via the Lexical command pipeline.
   */
  markdownTransformers?: Transformer[];
};

/**
 * Registers REPLACE_TEXT_COMMAND, INSERT_TEXT_COMMAND, and optionally
 * REPLACE_FROM_MARKDOWN_COMMAND for controlled document sync.
 */
export function TextReplacePlugin({
  markdownTransformers,
}: TextReplacePluginProps = {}) {
  const [editor] = useLexicalComposerContext();

  const replaceText = ({ text, focus }: ReplaceTextPayload) => {
    editor.update(
      () => {
        if (!focus) {
          $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        }

        const root = $getRoot();

        root.clear();

        const paragraphNode = $createParagraphNode();
        const textNode = $createTextNode(text);

        paragraphNode.append(textNode);

        root.append(paragraphNode);

        if (focus) {
          root.selectEnd();
        }
      },
      {
        discrete: true,
      },
    );

    return true;
  };

  const replaceMarkdown = ({ markdown, focus }: ReplaceFromMarkdownPayload) => {
    if (markdownTransformers === undefined) {
      return true;
    }

    editor.update(
      () => {
        if (!focus) {
          $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        }

        deserializeFromMarkdown(markdown, editor, markdownTransformers);
      },
      { discrete: true },
    );

    return true;
  };

  const insertText = ({ text }: InsertTextPayload) => {
    editor.update(
      () => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertText(text);
        }
      },
      {
        discrete: true,
      },
    );

    return true;
  };

  useEffect(() => {
    return editor.registerCommand(
      REPLACE_TEXT_COMMAND,
      replaceText,
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      INSERT_TEXT_COMMAND,
      insertText,
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);

  useEffect(() => {
    if (markdownTransformers === undefined) {
      return;
    }

    return editor.registerCommand(
      REPLACE_FROM_MARKDOWN_COMMAND,
      replaceMarkdown,
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor, markdownTransformers]);

  return null;
}
